import { useEffect, useRef, useState } from 'react';
import { CustomerType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { OpenBankingAccountVerifyStatus, OpenBankingIncomeQuery, useOpenBankingIncomeQuery } from '@graphql/index';
import { determineApplicationSteps } from '@pages/ApplicationForm/hooks';
import { useApplicationData, useOpenBankingSteps, useStepperActions } from '@provider';
import { formatDollarsWithoutCents } from '@steps/FinancePurchase/utils/getFinanceDetails';
import {
  APPLICATION_STEPS,
  OPEN_BANKING_POLLING_INTERVAL,
  OPEN_BANKING_TIMEOUT_DURATION,
  getApplicantTypeAndTrackingIdFromUrl,
} from '../../utils';

export type BankIncomeSource = OpenBankingIncomeQuery['openBankingIncome']['incomeSources'][0];
export type BankIncomeSources = Record<string, BankIncomeSource[]> | undefined;

function useEditToIncomeVerification() {
  const { onEdit } = useStepperActions();
  const { isOpenBankingConsentComplete, setIsRequestingManualIncomeEdit } = useOpenBankingSteps();
  const { quote, Finance } = useApplicationData(['quote', 'Finance']);

  const findIncomeVerificationStepIndex = () => {
    const customerType = quote?.financialProduct.customerType ?? CustomerType.Residential;
    const isCashApplication = Finance?.payment?.isCashApplication;
    const stepArray = determineApplicationSteps(customerType, isCashApplication, isOpenBankingConsentComplete);
    return stepArray.findIndex(step => step === APPLICATION_STEPS.INCOME_VERIFICATION);
  };

  const handleEditToIncomeVerification = () => {
    const incomeVerificationIndex = findIncomeVerificationStepIndex();
    if (incomeVerificationIndex !== -1) {
      setIsRequestingManualIncomeEdit(true);
      onEdit(incomeVerificationIndex);
    }
  };

  return handleEditToIncomeVerification;
}

const isAllConsentsSynced = (consents?: OpenBankingIncomeQuery['openBankingConsents']): boolean => {
  if (!Array.isArray(consents) || !consents?.length) {
    return false;
  }

  const isSynced: boolean = consents.every(consent => {
    if (!Array.isArray(consent.accounts)) {
      return false;
    }

    if (consent.accounts.length === 0) {
      return Boolean(consent.accountsSynced);
    }

    return consent.accounts.every(account => {
      if (!account) {
        return false;
      }
      if (account.status === OpenBankingAccountVerifyStatus.VERIFIED) {
        return Boolean(account.synced);
      }
      if (account.status === OpenBankingAccountVerifyStatus.FAILED) {
        return true;
      }
      return false;
    });
  });

  return isSynced;
};

export const getUniqueIncomeSources = (incomeSources: BankIncomeSource[]): BankIncomeSource[] => {
  const unique = new Map<string, BankIncomeSource>();

  for (const source of incomeSources) {
    const existing = unique.get(source.accountId);

    if (!existing) {
      unique.set(source.accountId, source);
    } else if (
      //  For duplicate bank accounts, prioritize those marked VERIFIED; if none are VERIFIED, select those marked FAILED
      source.status === OpenBankingAccountVerifyStatus.VERIFIED &&
      existing?.status !== OpenBankingAccountVerifyStatus.VERIFIED
    ) {
      unique.set(source.accountId, source);
    }
  }

  return Array.from(unique.values());
};

export const groupedIncomeSourcesByInstitution = (incomeSources?: BankIncomeSource[]): BankIncomeSources => {
  if (!incomeSources) {
    return {};
  }

  const grouped: BankIncomeSources = {};

  for (const source of incomeSources) {
    const { institutionId } = source;

    if (!grouped[institutionId]) {
      grouped[institutionId] = [];
    }

    grouped[institutionId].push(source);
  }

  return grouped;
};

const useOpenBankingIncomeDetail = ({
  userChoice = null,
}: {
  userChoice?: 'waiting' | 'continue' | 'cancel' | null;
}) => {
  const { trackingId: applicationId } = getApplicantTypeAndTrackingIdFromUrl();
  const { setOpenBankingIncomeSyncState } = useOpenBankingSteps();
  const [poolingState, setPoolingState] = useState<{
    startTime: number | null;
    enablePolling: boolean;
    userChoice: 'waiting' | 'continue' | 'cancel' | null;
  }>({
    startTime: null,
    enablePolling: false,
    userChoice: null,
  });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetState = () => {
    setPoolingState(prev => ({ ...prev, startTime: null, enablePolling: false, userChoice: null }));
    clearTimeoutHandler();
  };

  useEffect(() => {
    if (userChoice !== poolingState.userChoice) {
      setPoolingState(prev => ({ ...prev, userChoice }));

      if (userChoice === 'cancel') {
        setPoolingState(prev => ({ ...prev, enablePolling: false }));
      }
    }
  }, [userChoice, poolingState.userChoice]);

  const { data, isFetching, refetch } = useOpenBankingIncomeQuery(
    {
      applicationId: applicationId || '',
    },
    {
      enabled: poolingState.enablePolling && poolingState.userChoice !== 'cancel',
      retry: (failureCount, error) => {
        const messages: string[] =
          (error as { response?: { errors?: { message: string }[] } })?.response?.errors?.map(
            graphqlError => graphqlError.message,
          ) ?? [];
        if (messages.some(message => message.includes('an account that has been active for more than 30 days'))) {
          return false;
        }
        return failureCount < 5;
      },
      refetchInterval: data => {
        if (
          !poolingState.enablePolling ||
          poolingState.userChoice === 'cancel' ||
          isAllConsentsSynced(data?.openBankingConsents)
        ) {
          return false;
        }

        return OPEN_BANKING_POLLING_INTERVAL;
      },
      onSuccess: data => {
        if (isAllConsentsSynced(data?.openBankingConsents)) {
          resetState();
          setOpenBankingIncomeSyncState({ status: 'success' });

          datadogRum.addAction('Time taken to fetch the income successfully', {
            name: 'Time taken to fetch the income successfully',
            values: {
              timeTaken: poolingState.startTime ? (Date.now() - poolingState.startTime) / 1000 : 0,
              applicationId: applicationId,
            },
          });
        }
      },
      onError: () => {
        datadogRum.addAction('Error fetching income data', {
          applicationId: applicationId,
        });
        setOpenBankingIncomeSyncState({ status: 'idle' });
        resetState();
      },
    },
  );

  const handleTimeout = () => {
    if (!poolingState.startTime) return;

    if (poolingState.userChoice === 'cancel') {
      datadogRum.addAction('User cancelled income retrieval', {
        name: 'User cancelled income retrieval',
        values: {
          timeTaken: (Date.now() - poolingState.startTime) / 1000,
          applicationId: applicationId,
        },
      });
      resetState();
    }
  };

  const clearTimeoutHandler = () => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const getConsentDataLazy = () => {
    clearTimeoutHandler();
    const startTime = Date.now();
    setPoolingState(prev => ({ ...prev, startTime, enablePolling: true, userChoice: null }));

    refetch();
  };

  useEffect(() => {
    if (poolingState.startTime && poolingState.enablePolling) {
      timeoutRef.current = setInterval(handleTimeout, OPEN_BANKING_POLLING_INTERVAL);
    }
    return () => {
      clearTimeoutHandler();
    };
  }, [poolingState]);

  const openBankingData = data?.openBankingIncome;
  const uniqueIncomeSources = getUniqueIncomeSources(openBankingData?.incomeSources ?? []);
  const bankIncomeSources = groupedIncomeSourcesByInstitution(uniqueIncomeSources);

  const completedContent = [
    {
      title: '',
      items: [
        {
          label: 'Total monthly income',
          value: formatDollarsWithoutCents(openBankingData?.totalMonthlyIncome ?? 0) + ' monthly',
        },
      ],
    },
  ];

  return {
    completedContent,
    openBankingData,
    bankIncomeSources,
    getConsentDataLazy,
    startTime: poolingState.startTime,
    isOpenBankingDataFetching: poolingState.enablePolling || isFetching,
  };
};
export { useEditToIncomeVerification, useOpenBankingIncomeDetail };
